import gui from '@/webgl/dev/gui'
import Contrast from '@/webgl/postprocess/effects/contrast'
import Fetch from '@/webgl/postprocess/effects/fetch'
import Saturation from '@/webgl/postprocess/effects/saturation'
import FXAA from '@/webgl/postprocess/fxaa/fxaa'
import Post from '@/webgl/postprocess/post'
import Renderer from '@/webgl/Renderer'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'
import Displacement from '@/webgl/runner/rendering/effects/Displacement'
import Grain from '@/webgl/runner/rendering/effects/Grain'
import Pixelate from '@/webgl/runner/rendering/effects/Pixelate'

function msaaSampleFromDPI(dpi: number) {
  // return dpi < 2 ? 4 : 0;
  return 0
}

export function GetDefaultPostprocessSettings() {
  return {
    saturation: 1,
    contrast: 1,
    brightness: 1,
    bias: 1,
  }
}

export default class Postprocess implements ILoadable {
  post: Post
  saturation: Saturation
  contrast: Contrast
  displacement: Displacement
  grain: Grain
  pixelate: Pixelate
  fxaa: FXAA

  useFXAA: boolean = false

  get enabled(): boolean {
    return this.post.enabled
  }
  set enabled(value) {
    this.post.enabled = value
  }

  constructor(public readonly renderer: Renderer) {
    this.post = new Post(renderer.gl, {
      msaaSamples: msaaSampleFromDPI(this.renderer.glview.pixelRatio),
      // msaaSamples: 0,
      mipmap: false,
      alpha: true,
      hdr: false,
    })

    this.enabled = true

    this.fxaa = new FXAA(renderer.gl)

    const prms = GetDefaultPostprocessSettings()
    this.displacement = new Displacement()
    this.saturation = new Saturation(prms.saturation)
    this.contrast = new Contrast(prms.contrast, prms.brightness, prms.bias)
    this.grain = new Grain()
    this.pixelate = new Pixelate()

    this.post.add(new Fetch())
    this.post.add(this.pixelate)
    this.post.add(this.displacement)
    this.post.add(this.saturation)
    this.post.add(this.contrast)
    this.post.add(this.grain)

    /// #if _DEBUG
    const rootFolder = gui.folder('Postprocess')

    rootFolder.add(this, 'enabled')
    rootFolder.add(this, 'useFXAA')

    const sat = rootFolder.folder('Saturation')
    sat.add(this.saturation, 'amount', { min: 0, max: 2.0 })

    const contrast = rootFolder.folder('Contrast')
    contrast.add(this.contrast, 'contrast', { min: 0, max: 2.0 })
    contrast.add(this.contrast, 'brightness', { min: 0, max: 2.0 })
    contrast.add(this.contrast, 'bias', { min: 0, max: 2.0 })

    contrast.add(this.contrast, 'ctR', { min: 0, max: 1 })
    contrast.add(this.contrast, 'ctG', { min: 0, max: 1 })
    contrast.add(this.contrast, 'ctB', { min: 0, max: 1 })

    this.grain.setupGUI(rootFolder)
    this.displacement.setupGUI(rootFolder)
    this.pixelate.setupGUI(rootFolder)
    /// #endif
  }

  enableAntialiasing(value: boolean) {
    const updated = value !== this.post.msaaSamples > 0
    this.post.msaaSamples = value ? msaaSampleFromDPI(this.renderer.glview.pixelRatio) : 0
    if (updated) this.post.regenerateFbo()
  }

  async load(abort: AbortSignal): Promise<void> {
    await Promise.all([this.displacement.load(), this.grain.load()])
  }

  viewport(w: number, h: number) {
    this.post.preRender(w, h)
    this.fxaa.resize(w, h)
  }

  preRender(deltaTime: number) {
    this.pixelate.update(deltaTime)
    this.displacement.update(deltaTime)
    this.grain.update(deltaTime)
  }

  bindColor() {
    this.post.bindColor()
  }

  render() {
    if (this.useFXAA) {
      this.post.render(this.fxaa.fbo)
      this.renderer.gl.bindFramebuffer(this.renderer.gl.FRAMEBUFFER, null)
      this.fxaa.render()
    } else {
      this.post.render()
    }
  }
}
